import { codeBlocksInOrder, parseChatMarkdown } from './chatMarkdownParser.js';
/** The largest page the preview runs: 8 MiB, the largest file Gyro offers for download. */
export const PREVIEW_MAX_BYTES = 8 * 1024 * 1024;
/** How much of a file's start is read to decide whether its text is a web page. */
const SNIFF_CHARACTERS = 2048;
/** A name's extension, lower case, without the dot ('' when it has none). */
function extensionOf(name) {
    const base = name.split(/[\\/]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}
/** A file named as a web page: .html or .htm. */
export function isHtmlFileName(name) {
    const extension = extensionOf(name);
    return extension === 'html' || extension === 'htm';
}
/** A file whose text may be a web page Gyro offered as plain text: .txt. */
export function isTextFileName(name) {
    return extensionOf(name) === 'txt';
}
/**
 * Whether text is a web page: near its start (after a byte order mark, white space and any comments) it opens with
 * `<!doctype html` or an `<html` tag. A fragment (a button and a script) is not: only a code block is previewed as one.
 */
export function looksLikeHtmlDocument(text) {
    const head = text.slice(0, SNIFF_CHARACTERS).replace(/^\uFEFF/, '').trimStart();
    return /^(?:<!--[\s\S]*?-->\s*)*<(?:!doctype\s+html(?=[\s>])|html(?=[\s>]))/i.test(head);
}
/** A code block whose language label says HTML. */
export function isHtmlCodeLanguage(language) {
    const label = language.trim().toLowerCase();
    return label === 'html' || label === 'htm';
}
/** A file's name as the panel shows it: without the folder and the extension. */
export function pageNameFromFile(name) {
    const base = (name.split(/[\\/]/).pop() ?? name).trim();
    const dot = base.lastIndexOf('.');
    return (dot > 0 ? base.slice(0, dot) : base).trim() || 'Web page';
}
const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: '\'', nbsp: ' ' };
/** A page's <title>, as plain text with its white space collapsed, or null when it has none (or an empty one). */
export function htmlTitle(html) {
    const match = /<title[^>]*>([\s\S]*?)<\/title\s*>/i.exec(html.slice(0, 64 * 1024));
    if (!match)
        return null;
    const text = match[1]
        .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body) => {
        if (body[0] === '#') {
            const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
        }
        return ENTITIES[body.toLowerCase()] ?? entity;
    })
        .replace(/\s+/g, ' ')
        .trim();
    return text ? text.slice(0, 120) : null;
}
/**
 * A page name split into its title and a trailing version ("Check List Monger V1.2" is "Check List Monger" and "V1.2"):
 * V, v, ver or version followed by numbers, or a bare number in brackets. `version` is null when the name has none.
 */
export function splitVersion(name) {
    const match = /^(.*?)[\s_-]*((?<![A-Za-z0-9])(?:v|ver\.?|version)\s*\d+(?:\.\d+)*|\(\d+\))$/i.exec(name.trim());
    if (!match || !match[1].trim())
        return { base: name.trim(), version: null };
    return { base: match[1].trim(), version: match[2].replace(/\s+/g, ' ') };
}
/**
 * The version list's labels, in the order given (newest first). When every page shares one title ("Check List Monger
 * V1.2", "Check List Monger V1.1"), each is named by its version alone ("V1.2 (latest)", as drawn); otherwise by its name.
 * A name used twice gets its position in the list, so no two labels are the same.
 */
export function versionOptions(sources) {
    const split = sources.map((source) => splitVersion(source.name));
    const oneTitle = split.length > 1 && split.every((part) => part.version !== null && part.base.toLowerCase() === split[0].base.toLowerCase());
    const labels = sources.map((source, index) => (oneTitle ? split[index].version : source.name));
    const seen = new Map();
    for (const label of labels)
        seen.set(label, (seen.get(label) ?? 0) + 1);
    return sources.map((source, index) => {
        const label = seen.get(labels[index]) > 1 ? `${labels[index]} (${sources.length - index})` : labels[index];
        return { id: source.id, label: index === 0 ? `${label} (latest)` : label };
    });
}
/** The panel's title for a page: its name without a trailing version, as drawn ("Check List Monger"). */
export function previewTitle(name) {
    return splitVersion(name).base || name;
}
/** Bytes as text: UTF-8, a byte order mark dropped, a malformed sequence shown as a replacement character. */
export function decodePageBytes(bytes) {
    return new TextDecoder('utf-8').decode(bytes);
}
/** A base64 string's bytes (an attachment's `data`). Throws on text that is not base64. */
export function base64Bytes(data, limit = Number.POSITIVE_INFINITY) {
    const clipped = Number.isFinite(limit) ? data.slice(0, Math.ceil(limit / 3) * 4) : data;
    const binary = atob(clipped);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1)
        bytes[index] = binary.charCodeAt(index);
    return bytes;
}
/** An attachment's approximate size in bytes, from its base64 length. */
export function attachmentBytes(attachment) {
    const data = attachment.data ?? '';
    const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((data.length * 3) / 4) - padding);
}
/**
 * Whether an attachment still in the chat is a web page: named .html or .htm (or sent as text/html), or a .txt whose
 * start reads as a web page. Only the first bytes are decoded to decide.
 */
export function isWebPageAttachment(attachment) {
    if (attachment.kind === 'image' || typeof attachment.data !== 'string' || !attachment.data)
        return false;
    if (attachmentBytes(attachment) > PREVIEW_MAX_BYTES)
        return false;
    const name = attachment.name ?? '';
    if (isHtmlFileName(name) || attachment.mediaType.toLowerCase().split(';')[0].trim() === 'text/html')
        return true;
    if (!isTextFileName(name))
        return false;
    try {
        return looksLikeHtmlDocument(decodePageBytes(base64Bytes(attachment.data, SNIFF_CHARACTERS * 2)));
    }
    catch {
        return false;
    }
}
/** A file Gyro made whose name says web page (.html, .htm); a .txt needs its text read (`artifactNeedsSniff`). */
export function isHtmlArtifact(artifact) {
    if (!(artifact.sizeBytes <= PREVIEW_MAX_BYTES))
        return false;
    return isHtmlFileName(artifact.name) || artifact.mediaType.toLowerCase().split(';')[0].trim() === 'text/html';
}
/** A .txt file Gyro made, small enough to preview, whose text decides whether it is a web page. */
export function artifactNeedsSniff(artifact) {
    return artifact.sizeBytes > 0 && artifact.sizeBytes <= PREVIEW_MAX_BYTES && isTextFileName(artifact.name) && !isHtmlArtifact(artifact);
}
/** The key a file is known by in this chat (and in the sniff results). */
export function artifactKey(artifact) {
    return `file:${artifact.taskId}:${artifact.id}`;
}
/** The id of a code block's page: its reply and its position among the reply's code blocks, from 1. */
export function codeSourceId(replyKey, ordinal) {
    return `code:${replyKey}:${ordinal}`;
}
/** The id of an attachment's page: its message's position and its own. */
export function attachmentSourceId(messageIndex, attachmentIndex) {
    return `attachment:${messageIndex}:${attachmentIndex}`;
}
/** The reply key the chat already files a reply's files under (its start time, else its position). */
export function replyKeyOf(message, index) {
    return `${message.startedAt ?? index}`;
}
/** A finished reply's HTML code blocks (position among its code blocks from 1, text, name), by its text: parsed once. */
const codeBlockCache = new Map();
const CODE_BLOCK_CACHE_SIZE = 200;
function htmlCodeBlocks(markdown) {
    const cached = codeBlockCache.get(markdown);
    if (cached)
        return cached;
    const blocks = codeBlocksInOrder(parseChatMarkdown(markdown)).flatMap((block, index) => (isHtmlCodeLanguage(block.language) && block.text.trim()
        ? [{ ordinal: index + 1, text: block.text, name: htmlTitle(block.text) ?? 'HTML code' }]
        : []));
    if (codeBlockCache.size >= CODE_BLOCK_CACHE_SIZE)
        codeBlockCache.delete(codeBlockCache.keys().next().value);
    codeBlockCache.set(markdown, blocks);
    return blocks;
}
/**
 * Every web page of a chat, newest first. `fileVerdict` says whether a .txt file Gyro made reads as a web page ('web');
 * any other answer leaves it out. `extra` are the files made on this computer (Export > HTML), which no message lists.
 * A reply still being written offers no code block yet, and a failed reply none at all.
 */
export function previewSources(messages, fileVerdict, extra = []) {
    const found = [];
    const seen = new Set();
    const add = (source) => { if (!seen.has(source.id)) {
        seen.add(source.id);
        found.push(source);
    } };
    messages.forEach((message, index) => {
        if (message.welcome)
            return;
        const base = index * 1000;
        if (message.role === 'user') {
            (message.attachments ?? []).forEach((attachment, attachmentIndex) => {
                if (isWebPageAttachment(attachment)) {
                    add({ id: attachmentSourceId(index, attachmentIndex), kind: 'attachment', name: pageNameFromFile(attachment.name || 'Attached page'), order: base + attachmentIndex, attachment });
                }
            });
            return;
        }
        if (message.pending || message.streaming || message.error)
            return;
        const replyKey = replyKeyOf(message, index);
        if (message.content && /```|~~~/.test(message.content)) {
            for (const block of htmlCodeBlocks(message.content)) {
                add({ id: codeSourceId(replyKey, block.ordinal), kind: 'code', name: block.name, order: base + 100 + block.ordinal, html: block.text });
            }
        }
        (message.artifacts ?? []).forEach((artifact, artifactIndex) => {
            if (isHtmlArtifact(artifact) || (artifactNeedsSniff(artifact) && fileVerdict(artifact) === 'web')) {
                add({ id: artifactKey(artifact), kind: 'file', name: pageNameFromFile(artifact.name), order: base + 500 + artifactIndex, artifact });
            }
        });
    });
    for (const source of extra)
        add(source);
    return found.sort((left, right) => right.order - left.order);
}
//# sourceMappingURL=htmlPreview.js.map